import React, { Component } from 'react';
import MidiTrigger from './MidiTrigger.js';
import MidiEffect from './MidiEffect.js';
import {AbstractParameter} from 'parameters/AbstractParameter.js';
import Resolution from 'engine/Resolution.js';

const MAX_DELAYS = 8;

class MidiDelayEffect extends MidiEffect {
    constructor(track) {
        super('Midi Delay', track);

        this.delayTimeParameter =  new AbstractParameter(
            'delayTime', this, (o, x) => x,
            {
                documentation: "The size of the delay in 1/32th notes.",
                min: 1,
                max: 64, 
                unit: '', 
                defaultValue: 1,
            }
        );

        this.registerParameter(this.delayTimeParameter);


        this.repeatsParameter = new AbstractParameter(
            'repeats', this, (o, x) => [],
            {
                documentation: "The number of additional repeats that will be triggered for each trigger.",
                defaultValue: 1,
                min: 1,
                max: 32,
                unit: 'bars'
            });

        this.registerParameter(this.repeatsParameter);

        // how long the warp cycle goes for (in bars). effectively how
        // long it actually sounds
        this.wetParameter = new AbstractParameter(
            'wet', this, (o, x) => x,
            {
                documentation: "Controls the velocity of the delayed triggers vs. the original triggers.",
                min: 0,
                max: 1,
                unit: '', 
                defaultValue: 0.5,
            }
        );
        this.registerParameter(this.wetParameter);

        this.delays = [];
    }

    getVisualizedParameters() {
        return [this.delayTimeParameter, this.repeatsParameter];
    }

    getVisualizedParameters() {
        return [this.delayTimeParameter, this.repeatsParameter];
    }

    getText() {
        return this.delayTime + ', ' + this.repeats;
    }

    get documentation() {
        return "A delay-style midi effect that triggers the track at the given delayed rate.";
    }

    onNewStep() {
        this.triggers = [];
    }

    clear() {
        this.triggers = [];
        this.delays = [];
    }

    get wet() {
        return this.wetParameter.get();
    }

    get repeats() {
        return this.repeatsParameter.get();
    }

    get delayTime() {
        return this.delayTimeParameter.get();
    }

    set delayTime(x) {
        this.delayTimeParameter.set(x);
    }


    input(trigger, isLive=false) {
        if (this.delays.length > MAX_DELAYS) {
            // delete first delay
            this.delays = this.delays.slice(this.delays.length - MAX_DELAYS);
        }
        this.delays.push({
            stepNumber: trigger.stepNumber,
            trigger: this.copyTrigger(trigger),
            repeats: 0,
            elapsed: 0,
            });
        this.triggers.push(this.copyTrigger(trigger));
    }

    output(time, stepNumber, actualStepNumber) {
        let triggers = [];
        let delays = [];
        for (let i in this.delays) {
            let delay = this.delays[i];
            delay.elapsed++;
            if (this.shouldPlayDelay(stepNumber, delay)) {
                let stepData = this.getStep(delay.trigger.stepData, undefined, undefined, false);
                stepData.velocity = stepData.velocity * (this.repeats - delay.repeats) / this.repeats;
                stepData.velocity *= this.wet;
                let newTrigger = new MidiTrigger(stepData, time, stepNumber);
                if (stepData.velocity > 0) {
                    triggers.push(newTrigger);
                }
                delay.repeats++;

                if (delay.repeats >= this.repeats) {
                    continue;
                }
            }
            delays.push(delay);
        }

        let originalTriggers = this.triggers;
        for (let i in originalTriggers) {
            let stepData = this.getStep(originalTriggers[i].stepData, undefined, undefined, true);
            stepData.velocity *= (1 - this.wet);
            originalTriggers[i].stepData = stepData;
        }
        triggers = triggers.concat(originalTriggers);
        this.delays = delays;

        return triggers;
    }

    shouldPlayDelay(stepNumber, delay) {
        let durationElapsed = delay.elapsed - 1;
        if (durationElapsed === 0) {
            return false;
        }
        if (durationElapsed % Math.floor(this.delayTime) === 0) {
            return true;
        }

        return false;
    }

    getCopy(track, ignoreId=true) {
        let effect = new MidiDelayEffect(track);
        effect.fromJson(this.getJson(), ignoreId);
        return effect;
    }
}

export default MidiDelayEffect;
